import { useAuth } from "@/context/AuthContext";
import { useLocation } from "react-router-dom";
import { Users, X } from "lucide-react";

// Routes where the View-as facilitator toggle should NEVER appear.
// These are public/marketing pages — showing facilitator chrome here would
// confuse search-engine visitors and leak the impression that they are in
// an authenticated session.
const PUBLIC_ROUTES = new Set([
  "/",
  "/about",
  "/founder",
  "/support",
  "/login",
  "/register",
]);

/**
 * View-as toggle for facilitators & admins.
 * Lets them switch between the men's and women's curriculum views
 * (themes + API filtering) without altering their own account.
 */
export default function ViewAsToggle() {
  const { user, canOverride, viewProgram, setViewProgram, effectiveProgram, isViewingOther } = useAuth();
  const { pathname } = useLocation();
  if (!user || !canOverride) return null;
  // Hide on public/marketing routes so visitors don't see facilitator-only UI.
  if (PUBLIC_ROUTES.has(pathname)) return null;

  const current = effectiveProgram || "men";
  const opposite = current === "men" ? "women" : "men";

  const setView = (p) => {
    // null = follow your own account; anything else = override
    if (p === user.program) setViewProgram(null);
    else setViewProgram(p);
  };

  return (
    <>
      {/* Banner when actively overriding */}
      {isViewingOther && (
        <div
          className="bg-[#C9A961] text-[#0A0908] text-sm py-2 px-4 flex items-center justify-center gap-3 flex-wrap"
          data-testid="view-as-banner"
        >
          <Users className="w-4 h-4" strokeWidth={2} />
          <span>
            You're viewing the <strong>{current === "women" ? "Women's & Girls'" : "Men's & Young Men's"}</strong> curriculum as a {user.role}.
          </span>
          <button
            onClick={() => setView(user.program)}
            className="inline-flex items-center gap-1 underline font-semibold hover:no-underline"
            data-testid="view-as-clear"
          >
            <X className="w-3.5 h-3.5" /> Return to my own ({user.program})
          </button>
        </div>
      )}

      {/* Compact toggle pill (visible everywhere a facilitator/admin is signed in) */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 pt-3" data-testid="view-as-toggle">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="overline text-xs text-[#8A8782]">View as</span>
          <div className="inline-flex rounded-full border border-[#C9A961]/30 overflow-hidden text-xs">
            <button
              onClick={() => setView("men")}
              className={`px-3 py-1.5 transition ${
                current === "men"
                  ? "bg-[#C9A961] text-[#0A0908]"
                  : "text-[#D1CFC9] hover:bg-[#C9A961]/10"
              }`}
              data-testid="view-as-men"
            >
              Men's
            </button>
            <button
              onClick={() => setView("women")}
              className={`px-3 py-1.5 transition ${
                current === "women"
                  ? "bg-[#C9A961] text-[#0A0908]"
                  : "text-[#D1CFC9] hover:bg-[#C9A961]/10"
              }`}
              data-testid="view-as-women"
            >
              Women's
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
